import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useRef } from "react";
import { useFollowStore } from "@/api/queries/stores";
import type { Store } from "@/api/types";
import { isSafeReturnTo } from "@/domain/identity";
import { toast } from "@/stores/toast";
import { toggleStoreFollow } from "./store-follow-toggle";

/** Store follow toggle for a rendered store; the decision logic lives in store-follow-toggle.ts. */
export function useStoreFollowToggle(store: Store) {
  const follow = useFollowStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const busy = useRef(false);

  const toggle = () => {
    const returnTo = isSafeReturnTo(location.href) ? location.href : undefined;
    return toggleStoreFollow({
      queryClient,
      store,
      busy,
      isPending: () => follow.isPending,
      follow: (variables, callbacks) => follow.mutate(variables, callbacks),
      join: () => void navigate({ to: "/join", search: { returnTo } }),
      claim: () => void navigate({ to: "/welcome", search: { step: "identity", returnTo } }),
      toastError: (message) => toast.error(message),
    });
  };

  return { toggle, isPending: follow.isPending };
}
